import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from './store';
import type { Role, MilestoneMap1, UserWithAccessAndRole } from '@mm-shared/types/MilestoneMap1';
import type { UserProfile1 } from '@mm-shared/types/UserProfile1';
import type { Human } from '@mm-shared/types/Human';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';

/**
 * SELECTOR PATTERNS DOCUMENTATION
 * ===============================
 *
 * This file contains Redux selectors for accessing and computing data from the store.
 * We use two main patterns:
 *
 * ## 1. DIRECT STATE ACCESS
 * Use for simple property getters with no computation:
 * ```typescript
 * export const selectUserProfile = (state: RootState) => state.map3Data.userProfile;
 * ```
 *
 * ## 2. CREATESELECTOR (MEMOIZED COMPUTATION)
 * Use when you need computation, parameters, or memoization:
 * ```typescript
 * export const selectMapByShortId = createSelector(
 *   // INPUT SELECTORS: Define what data you need as inputs
 *   [selectAllMilestoneMaps, (_state: RootState, mapShortId: string) => mapShortId],
 *   // OUTPUT FUNCTION: Receives the results of input selectors as arguments
 *   (milestoneMaps, mapShortId) => {
 *     return milestoneMaps.find(m => getShortIdFromUuid(m.uuid) === mapShortId);
 *   }
 * );
 * ```
 *
 * ## createSelector Parameter Flow:
 * 1. **Input Selectors Array**: Functions that extract/pass data to the output function
 * 2. **Output Function**: Receives input selector results as separate arguments
 * 3. **Memoization**: Only re-runs when input selector results change
 *
 * ## When to use createSelector:
 * - ✅ Computation (find, filter, transform)
 * - ✅ Parameters needed (mapShortId, mapUuid)
 * - ✅ Derived data from other selectors
 * - ✅ Performance-sensitive operations
 *
 * ## When to use direct access:
 * - ✅ Simple property getters
 * - ✅ No computation needed
 * - ✅ Direct state.slice.property access
 */

/**
 * Basic selectors for accessing Redux state
 */
export const selectUserProfile = (state: RootState): UserProfile1 => state.map3Data.userProfile;

export const selectAllHumans = (state: RootState): Human[] => state.map3Data.humans;

/**
 * Find a Human doc linked to the current user's Firebase Auth uid.
 * Returns the Human if found, or undefined if no Human exists for this uid.
 */
export const selectHumanByUid = createSelector(
  [selectAllHumans, (_state: RootState, uid: string) => uid],
  (humans, uid) => humans.find((h) => h.uid === uid),
);

export const selectAllMilestoneMaps = (state: RootState): MilestoneMap1[] =>
  state.map3Data.milestoneMaps;

/**
 * Selector to find a milestone map by shortId
 *
 * @param state - Redux state (passed by useSelector)
 * @param mapShortId - The short ID of the map (last 10 characters of UUID)
 * @returns The milestone map with matching shortId, or null if not found
 *
 * Usage: useSelector((state) => selectMapByShortId(state, mapShortId))
 */
export const selectMapByShortId = createSelector(
  [
    // INPUT SELECTOR 1: Gets all milestone maps from state
    selectAllMilestoneMaps,
    // INPUT SELECTOR 2: Passes through the mapShortId parameter unchanged
    (_state: RootState, mapShortId: string) => mapShortId,
  ],
  // OUTPUT FUNCTION: Receives (milestoneMaps, mapShortId) as separate arguments
  (milestoneMaps, mapShortId) => {
    const map = milestoneMaps.find((m) => getShortIdFromUuid(m.uuid) === mapShortId);
    if (!map) {
      console.warn(`Map not found for shortId: ${mapShortId}`);
      return null;
    }
    return map;
  },
);

/**
 * Alternative selector to find a milestone map by UUID
 *
 * @param state - Redux state (passed by useSelector)
 * @param mapUuid - The full UUID of the map
 * @returns The milestone map with matching UUID, or null if not found
 *
 * Usage: useSelector((state) => selectMapByUuid(state, mapUuid))
 */
export const selectMapByUuid = createSelector(
  [
    // INPUT SELECTOR 1: Gets all milestone maps from state
    selectAllMilestoneMaps,
    // INPUT SELECTOR 2: Passes through the mapUuid parameter unchanged
    (_state: RootState, mapUuid: string) => mapUuid,
  ],
  // OUTPUT FUNCTION: Receives (milestoneMaps, mapUuid) as separate arguments
  (milestoneMaps, mapUuid) => {
    const map = milestoneMaps.find((m) => m.uuid === mapUuid);
    if (!map) {
      console.warn(`Map not found for uuid: ${mapUuid}`);
      return null;
    }
    return map;
  },
);

/**
 * Core selector that determines a user's role for a specific map (by UUID)
 *
 * @param state - Redux state (passed by useSelector)
 * @param mapUuid - The full UUID of the map
 * @returns User's role ('owner' | 'editor' | 'commenter' | 'viewer') or null if no access
 *
 * Usage: useSelector((state) => selectUserRoleForMap(state, mapUuid))
 */
export const selectUserRoleForMap = createSelector(
  [
    // INPUT SELECTOR 1: Gets the specific map by UUID (returns MilestoneMap1 | null)
    selectMapByUuid,
    // INPUT SELECTOR 2: Gets current user profile (returns UserProfile1)
    selectUserProfile,
  ],
  // OUTPUT FUNCTION: Receives (map, userProfile) as separate arguments
  (map, userProfile): Role | null => {
    if (!map) {
      console.warn('Cannot determine user role: map not found');
      return null;
    }

    if (!userProfile?.uid) {
      console.warn('Cannot determine user role: user not authenticated');
      return null;
    }

    // Check if user is the owner
    if (map.owner.uid === userProfile.uid) {
      return 'owner';
    }

    // Check if user is in usersWithAccess array
    const userAccess = map.usersWithAccess?.find(
      (u: UserWithAccessAndRole) => u.uid === userProfile.uid,
    );
    if (userAccess) {
      return userAccess.role;
    }

    // User not found in access list
    console.warn(`User ${userProfile.uid} not found in access list for map ${map.shortId}`);
    return null;
  },
);

/**
 * ROLE SELECTORS - Check what role a user has (exclusive, single role)
 * These check the user's actual role assignment on the map
 */

/**
 * @param state - Redux state
 * @param mapUuid - The full UUID of the map
 * @returns true if user's role is 'owner'
 */
export const selectIsOwner = createSelector(
  [selectUserRoleForMap], // INPUT: Gets role for the map
  (role): boolean => role === 'owner', // OUTPUT: Returns true if role is 'owner'
);

/**
 * @param state - Redux state
 * @param mapUuid - The full UUID of the map
 * @returns true if user's role is 'editor'
 */
export const selectIsEditor = createSelector(
  [selectUserRoleForMap], // INPUT: Gets role for the map
  (role): boolean => role === 'editor', // OUTPUT: Returns true if role is 'editor'
);

/**
 * @param state - Redux state
 * @param mapUuid - The full UUID of the map
 * @returns true if user's role is 'commenter'
 */
export const selectIsCommenter = createSelector(
  [selectUserRoleForMap], // INPUT: Gets role for the map
  (role): boolean => role === 'commenter', // OUTPUT: Returns true if role is 'commenter'
);

/**
 * @param state - Redux state
 * @param mapUuid - The full UUID of the map
 * @returns true if user's role is 'viewer'
 */
export const selectIsViewer = createSelector(
  [selectUserRoleForMap], // INPUT: Gets role for the map
  (role): boolean => role === 'viewer', // OUTPUT: Returns true if role is 'viewer'
);

/**
 * Convenience selector that combines role checks for easy component usage
 *
 * @param state - Redux state
 * @param mapUuid - The full UUID of the map
 * @returns Object with role and all boolean flags combined
 *
 * Usage: useSelector((state) => selectUserRoleInfo(state, mapUuid))
 * Returns: { role, isOwner, isEditor, isCommenter, isViewer, hasAccess }
 */
export const selectUserRoleInfo = createSelector(
  [
    // INPUT SELECTORS: Get role and all boolean flags
    selectUserRoleForMap, // Returns Role | null
    selectIsOwner, // Returns boolean
    selectIsEditor, // Returns boolean
    selectIsCommenter, // Returns boolean
    selectIsViewer, // Returns boolean
  ],
  // OUTPUT FUNCTION: Receives all 5 values and combines them into one object
  (role, isOwner, isEditor, isCommenter, isViewer) => ({
    role,
    isOwner,
    isEditor,
    isCommenter,
    isViewer,
    hasAccess: role !== null, // Computed: true if user has any role
  }),
);
